import { IOption } from './IOption'
import { IPropertyFilter } from './IPropertyFilter'

export type MultiSelect = {
  mode: 'any' | 'all' | 'none'
  options: IOption[]
}

const nullableMultiSelectFilters: string[] = ['importId']

export const isValidMultiSelect = ({ options }: MultiSelect): boolean =>
  options?.length > 0

export interface IMultiSelectFilter<T> extends IPropertyFilter<T> {
  type: 'multiSelect'
  value: MultiSelect
  propertyIsString: boolean
  propertyIsArray: boolean
  nestedPropertyName?: string
  startsWith?: boolean
  hideModeSelectField?: boolean
  hideAllMode?: boolean
}

export const isMultiSelectFilter = <T>(
  filter: IPropertyFilter<T>,
): filter is IMultiSelectFilter<T> => filter.type === 'multiSelect'

export const normalizeMultiSelectFilter = <T>({
  propertyName,
  nestedPropertyName,
  propertyIsArray,
  propertyIsString,
  startsWith,
  value: { mode, options },
}: IMultiSelectFilter<T>): string => {
  if (options.length === 0) return ''
  const propertyNameStr = String(propertyName)

  const prefix = !propertyIsArray
    ? `${propertyNameStr}${
        !!nestedPropertyName ? `/${nestedPropertyName}` : ''
      }`
    : `x/${nestedPropertyName}`

  // when selecting none w/ an array property
  // build the string as though mode is any, then invert the whole filter
  const operation = mode === 'none' && !propertyIsArray ? 'ne' : 'eq'

  let joinMode =
    mode === 'any' ||
    (mode === 'all' && startsWith) ||
    (mode === 'none' && propertyIsArray)
      ? ' or '
      : ' and '

  const optStrings = options.map(({ value }) => {
    const v = propertyIsString ? `'${value}'` : value
    if (startsWith) {
      return `startsWith(${prefix}, ${v}) ${operation} true`
    }
    return `${prefix} ${operation} ${v}`
  })

  if (
    nullableMultiSelectFilters.includes(propertyNameStr) &&
    operation == 'ne'
  ) {
    optStrings.push(`${propertyNameStr} eq null`)
    joinMode = ' or '
  }

  if (!propertyIsArray) {
    const filterString = optStrings.join(joinMode)
    return `(${filterString})`
  } else {
    if (mode === 'all') {
      // exclude records with empty array
      const excludeEmpty = `and ${propertyNameStr}/any()`

      const filterString = optStrings
        .map((o) => `${propertyNameStr}/any(x: ${o})`)
        .join(joinMode)

      return `${filterString} ${excludeEmpty}`
    }

    const optString = optStrings.join(joinMode)

    const filterString = `(${propertyNameStr}/any(x: ${optString}))`

    return mode === 'none' ? `(not${filterString})` : filterString
  }
}
